/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import {
    SecurityHelper,
    Stack,
    Box,
    Icon,
    Heading,
    Button,
    RecaptchaEnterprise,
    RecaptchaActions,
    TextBlock,
    ModalActions,
    ButtonGroup,
    useFlags,
    HtmlParser,
} from 'sarsaparilla';
import { authModalActions } from 'shared-ui/auth/redux';
import { EnterpriseRecaptchaModal } from 'shared-ui/components/RecaptchaModal/EnterpriseRecaptchaModal';

import { BookingForm } from '../../components/TourDetailsPage/BookingForm';
import SpinnerModal from '../../components/TourDetailsPage/SpinnerModal';
import { getCreateReservationUrl } from '../../api/endpoints';
import { convertServerErrorToDisplayError } from '../../utils/convertServerErrorToDisplayError';
import { setDateFilter } from '../../actions/filtersActions';

import {
    ERR_V2_REQUIRED,
    ERR_RATE_LIMIT_EXCEEDED,
    ERR_YOU_LOOK_FAMILIAR,
    ERR_BUSY,
} from '../../constants';
import { showGuestCheckout, showNoTicketRequired } from '../../utils/launchDarklyUtils';

const CHALLENGE_V2_REGION = 'WEST';
const CHALLENGE_ENTERPRISE_REGION = 'ENTERPRISE';
const V2_CAPTCHA = 'V2CAPTCHA';
const V3_CAPTCHA = 'V3CAPTCHA';
const RECAPTCHA_ACTION = 'timedEntryTicketDetailsPage';
const RETRY_DELAY = 1000; // 1 sec
const MAX_RETRIES = 5;
const SPINNER_MESSAGES = [
    "<p>There are a lot of visitors trying to purchase the same ticket(s) you requested.</p><p>The system is working as intended and is automatically re-trying to secure your chosen date/time. To increase your chance of success, stay on this page (don't refresh or click the Back button).</p>",
    "<p>Don't worry! We are processing requests in the order we receive them and the system is still automatically re-trying to secure your chosen date/time.</p><p> You can click cancel to close this message and try a less popular date or time.</p>",
    'We appreciate your patience. Click Try Again to request the same date and time, or close this message to try and request a less popular date or time.',
];
const SPINNER_HEADINGS = [
    'Thank you for your patience!',
    'There are a high volume of requests for this ticket right now.',
    "Your request has reached the re-try limit and couldn't be completed.",
];

export function BookingFormContainer() {
    const [bookingFormState, setBookingFormState] = useState({
        bookingError: {
            message: '',
            type: '',
        },
        openCalendar: false,
    });
    const [didAttemptBuy, setAttemptBuy] = useState(false);
    const [counts, setCounts] = useState();
    const [time, setTime] = useState();
    const [isSpinnerModalOpen, setSpinnerModalOpen] = useState(false);
    const [isRecaptchaModalOpen, setRecaptchaModalOpen] = useState(false);
    const [isFetching, setFetching] = useState(false);
    const [blockedDates, setBlockedDates] = useState([]);
    const [verification, setVerification] = useState('');
    const [spinnerMessage, setSpinnerMessage] = useState('');
    const [spinnerHeading, setSpinnerHeading] = useState(SPINNER_HEADINGS[0]);
    const [retryNumber, setRetryNumber] = useState(0);
    const abortRef = useRef(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    const { facilityId, tourId } = useParams();
    const spinnerModalSize = isFetching ? 'sm' : 'md';

    const facilityTimezone = useSelector(
        (state) => state.timedEntry.facility.facility_timezone
    );
    const isLoggedIn = useSelector((state) => !!state.auth?.account?.access_token);
    const dateM = useSelector((state) => state.timedEntry.filters?.date);
    const tourTimesData = useSelector((state) => state.timedEntry.tourTimes);
    const onRecaptchaChange = (value) => {
        setVerification(value);
    };

    const userEmail = SecurityHelper.getAccount().email;
    const {
        facilitiesUsingGuestCheckout,
        testRateLimiting: testRateLimitingFlag,
        facilitiesUsingNoTicketRequired,
    } = useFlags();

    const tourTimes = useMemo(
        () => tourTimesData?.data?.[tourId] || [],
        [tourId, tourTimesData?.data]
    );

    const hasOnlyWalkupTourTimes = useMemo(() => {
        let onlyWalkupTourTimes = [];
        tourTimes?.forEach((tourTime) => {
            if (
                !tourTime?.availableCount &&
                !tourTime?.unreleasedCount &&
                !!tourTime?.availableWalkupCount
            ) {
                onlyWalkupTourTimes = onlyWalkupTourTimes.concat(tourTime);
            }
        });

        return !!onlyWalkupTourTimes?.length;
    }, [tourTimes]);

    const guestCheckoutEnabled = useMemo(() => {
        let isGuestCheckoutEnabled = false;
        if (dateM) {
            const todaySelected = moment(dateM).isSame(
                moment().tz(facilityTimezone),
                'day'
            );

            isGuestCheckoutEnabled =
                showGuestCheckout(facilityId, facilitiesUsingGuestCheckout) &&
                todaySelected;
        }

        return isGuestCheckoutEnabled;
    }, [dateM, facilitiesUsingGuestCheckout, facilityId, facilityTimezone]);

    const showLoginNotice = useMemo(
        () => !!dateM && !guestCheckoutEnabled,
        [dateM, guestCheckoutEnabled]
    );

    const showWalkupNotice = useMemo(
        () => !!dateM && hasOnlyWalkupTourTimes,
        [dateM, hasOnlyWalkupTourTimes]
    );

    const showNoTicketRequiredNotice = useMemo(
        () =>
            showNoTicketRequired(facilityId, facilitiesUsingNoTicketRequired) &&
            !!dateM &&
            tourTimes.length === 0,
        [dateM, facilityId, facilitiesUsingNoTicketRequired, tourTimes]
    );

    const makeBookingApiRequest = async (system) => {
        const filteredCounts = { ...counts };

        for (const property in counts) {
            if (!counts[property]) {
                delete filteredCounts[property];
            }
        }

        const reservationRequest = {
            reservation: {
                facility_id: facilityId,
                tour_id: tourId,
                status: 'DRAFT',
                tour_date: dateM.format('YYYY-MM-DD'),
                tour_time: time,
                ticket_count_by_guest_type_id: filteredCounts,
                passes_by_guest_type_id: {},
                user_email: userEmail,
            },
            reservationoptions: {
                targetstatus: 'HOLD',
            },
        };

        if (system) {
            reservationRequest.system = system;
        }

        // Do the thing where we send the reservation to the api
        // No try catch because a thrown error will be caught by attemptBookingWithRetry
        // so we can see if we need to retry
        const { data } = await axios.post(getCreateReservationUrl(), reservationRequest, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        });

        const reservationId = data?.reservation_id;

        if (!reservationId) {
            throw new Error('Reservation did not succeed');
        } else {
            return reservationId;
        }
    };

    const processV3 = () => {
        return new Promise((resolve, reject) => {
            recaptchaRef?.current
                ?.executeAsync()
                .then((token) => {
                    const recaptcha = {
                        code: token,
                        action: RECAPTCHA_ACTION,
                        region: CHALLENGE_ENTERPRISE_REGION,
                    };
                    makeBookingApiRequest(recaptcha)
                        .then((response) => resolve(response))
                        .catch((error) => reject(error));
                })
                .catch(() => {
                    const recaptcha = {
                        code: '',
                        action: RECAPTCHA_ACTION,
                        region: CHALLENGE_ENTERPRISE_REGION,
                    };
                    makeBookingApiRequest(recaptcha)
                        .then((response) => resolve(response))
                        .catch((error) => reject(error));
                });
        });
    };

    const processV2 = () => {
        const recaptcha = {
            section: RECAPTCHA_ACTION,
            code: verification,
            region: CHALLENGE_V2_REGION,
        };

        return makeBookingApiRequest(recaptcha);
    };

    const handleError = (error, type, retryCount, testRateLimiting) => {
        const errorMsg = error?.response?.data?.error || error.message;

        const wasRateLimited =
            errorMsg.includes(ERR_BUSY) || errorMsg === ERR_RATE_LIMIT_EXCEEDED;

        const newErrorMsg = convertServerErrorToDisplayError(errorMsg);
        setBookingFormState((prevState) => ({
            ...prevState,
            bookingError: {
                ...prevState?.bookingError,
                message: newErrorMsg,
                type: wasRateLimited ? ERR_BUSY : '',
            },
        }));

        // Captcha v3 failed, so we open Recaptcha Modal
        if (errorMsg === ERR_V2_REQUIRED) {
            setSpinnerModalOpen(false);
            setRecaptchaModalOpen(true);
            dispatch(RecaptchaActions.expireVerification());
        }

        // Was rate-limited or busy >> this trigger a retry
        else if (wasRateLimited && retryCount <= MAX_RETRIES && !abortRef.current) {
            setSpinnerMessage(retryCount < 3 ? SPINNER_MESSAGES[0] : SPINNER_MESSAGES[1]);
            setSpinnerHeading(retryCount < 3 ? SPINNER_HEADINGS[0] : SPINNER_HEADINGS[1]);
            setRetryNumber(retryCount);
            setTimeout(() => {
                attemptBookingWithRetry({ type, retryCount, testRateLimiting });
            }, RETRY_DELAY);
        }

        // If over-booking error, add date to blocked dates. Spinner modal stays
        // open to show the error
        else if (errorMsg.includes(ERR_YOU_LOOK_FAMILIAR)) {
            setFetching(false);
            setBlockedDates((prev) => {
                prev.push(dateM.format('YYYY-MM-DD'));
                return prev;
            });
        }

        // We hit the max retries or other error. SpinnerModal stays open to
        // show the error
        else {
            setFetching(false);
            setRetryNumber(0);
        }
    };

    async function attemptBookingWithRetry({
        type,
        retryCount = 0,
        testRateLimiting = false,
    }) {
        setFetching(true);
        setSpinnerModalOpen(true);
        setRecaptchaModalOpen(false);

        if (testRateLimiting) {
            // Wait for 5 seconds to emulate CreateReservation latency during on-sale
            await new Promise((resolve) => {
                window.setTimeout(resolve, 5000);
            });
            const error = {
                message:
                    'Too many people are requesting this tour at this time, please try again',
            };
            handleError(error, type, retryCount + 1, testRateLimiting);
            return;
        }

        let processFunc;

        switch (type) {
            case V3_CAPTCHA:
                processFunc = processV3;
                break;
            case V2_CAPTCHA:
                processFunc = processV2;
                break;
            default:
                processFunc = makeBookingApiRequest;
        }

        processFunc()
            .then((reservationId) => {
                setFetching(false);
                setSpinnerModalOpen(false);
                navigate(`/timed-entry/reservation/${reservationId}`);
            })
            .catch((error) => {
                handleError(error, type, retryCount + 1, testRateLimiting);
            });
    }

    /**
     * Handler for 'Buy Tickets' button
     */
    const onBuyClick = (testRateLimiting = false) => {
        setBookingFormState((prevState) => ({
            ...prevState,
            bookingError: {
                message: '',
                type: '',
            },
        }));
        setSpinnerMessage('');
        setSpinnerHeading(SPINNER_HEADINGS[0]);
        abortRef.current = false;

        if (testRateLimiting) {
            attemptBookingWithRetry({ V3_CAPTCHA, retryCount: 0, testRateLimiting });
            return;
        }

        if (SecurityHelper.isLoggedIn() || SecurityHelper.isGuest()) {
            setAttemptBuy(false);
            const type = V3_CAPTCHA;
            attemptBookingWithRetry({ type, retryCount: 0, testRateLimiting });
        } else {
            setAttemptBuy(true);
            if (guestCheckoutEnabled) {
                dispatch(authModalActions.openLoginWithGuestModal());
            } else {
                dispatch(authModalActions.openLoginModal());
            }
        }
    };

    const handleBookingErrorModalClose =
        (resetDateFilter = false) =>
        () => {
            const date = !resetDateFilter ? dateM : null;
            setSpinnerModalOpen(false);
            abortRef.current = true;
            setFetching(false);
            dispatch(setDateFilter(date));
            if (resetDateFilter) {
                setBookingFormState((prevState) => ({
                    ...prevState,
                    bookingError: {
                        message: '',
                        type: '',
                    },
                    openCalendar: true,
                }));
            }
            setSpinnerMessage('');
            setSpinnerHeading(SPINNER_HEADINGS[0]);
            setRetryNumber(0);
        };

    /**
     * If the user attempts to buy a ticket but isn't logged in, they will get the login
     * modal. Once they are logged in, we want to attempt the purchase again
     */
    useEffect(() => {
        let isMounted = true;

        if (isLoggedIn && didAttemptBuy) {
            onBuyClick();
            if (isMounted) {
                setAttemptBuy(false);
            }
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, didAttemptBuy]);

    const isDateBlocked = blockedDates.includes(dateM?.format('YYYY-MM-DD'));

    const handleDatePickerFocus = useCallback(() => {
        setBookingFormState((prevState) => ({
            ...prevState,
            openCalendar: false,
        }));
    }, []);

    return (
        <>
            <RecaptchaEnterprise
                ref={recaptchaRef}
                action={RECAPTCHA_ACTION}
                size="invisible"
                sitekey={process.env.RECAPTCHA_ENTERPRISE_KEY_INVIS}
            />
            <BookingForm
                isDateBlocked={isDateBlocked}
                isBookingInProgress={isFetching}
                onBuyClick={onBuyClick}
                bookingError={bookingFormState?.bookingError?.message}
                counts={counts}
                setCounts={setCounts}
                time={time}
                setTime={setTime}
                openCalendar={bookingFormState?.openCalendar}
                showLoginNotice={showLoginNotice}
                showWalkupNotice={showWalkupNotice}
                showNoTicketRequiredNotice={showNoTicketRequiredNotice}
                facilityId={facilityId}
                guestCheckoutEnabled={guestCheckoutEnabled}
                onFocus={handleDatePickerFocus}
            />
            <SpinnerModal
                heading={spinnerHeading}
                isOpen={isSpinnerModalOpen}
                isSpinning={isFetching}
                message={spinnerMessage}
                retryNumber={retryNumber}
                maxRetries={MAX_RETRIES}
                shouldShowCloseButton={true}
                size={spinnerModalSize}
                onRequestClose={handleBookingErrorModalClose()}
                handleBookingErrorModalClose={handleBookingErrorModalClose}
            >
                <Stack className="mt-6">
                    <Box alignX="center">
                        {bookingFormState?.bookingError?.type === ERR_BUSY ? (
                            <Icon iconName="info-outline" size="xl" />
                        ) : (
                            <Icon iconName="warning" size="xl" color="red" />
                        )}
                    </Box>
                    <Heading
                        headingLevel={1}
                        appearance="h4"
                        style={{ textAlign: 'center' }}
                    >
                        {bookingFormState?.bookingError?.type === ERR_BUSY
                            ? SPINNER_HEADINGS[SPINNER_HEADINGS.length - 1]
                            : 'Sorry, we couldn’t make this reservation'}
                    </Heading>
                    <TextBlock className="mt-3">
                        <div>
                            <HtmlParser
                                html={
                                    bookingFormState?.bookingError?.type === ERR_BUSY
                                        ? SPINNER_MESSAGES[SPINNER_MESSAGES.length - 1]
                                        : bookingFormState?.bookingError?.message || ''
                                }
                            />
                        </div>
                    </TextBlock>
                    {bookingFormState?.bookingError?.type === ERR_BUSY && (
                        <ModalActions>
                            <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                                <Button
                                    appearance="tertiary"
                                    onClick={handleBookingErrorModalClose(true)}
                                    gaTrackingId="272451767375"
                                >
                                    Choose a Different Date
                                </Button>
                                <Button
                                    onClick={() => onBuyClick(testRateLimitingFlag)}
                                    gaTrackingId="649848103055"
                                >
                                    Try Again
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    )}
                </Stack>
            </SpinnerModal>

            <EnterpriseRecaptchaModal
                isOpen={isRecaptchaModalOpen}
                action={RECAPTCHA_ACTION}
                isSolved={verification !== ''}
                onRecaptchaChange={onRecaptchaChange}
                closeModal={() => {
                    attemptBookingWithRetry({ type: V2_CAPTCHA });
                }}
            />
        </>
    );
}

// cSpell:ignore reservationoptions, targetstatus, nocaptcha, recaptchav, RECAPTCHA_ENTERPRISE_KEY_INVIS
